/**
 * No-PII funnel reporter. Every call returns immediately and swallows failure.
 * A missing Web Crypto API, blocked storage, or a failed post must not affect
 * Start. Dev servers do not post. A build made with `VITE_FUNNEL_EVENTS=false`
 * does not post. Event names match `cdk/allow-list.ts`.
 */

const STORAGE_KEY = 'sash.funnel.v1';
const ATTEMPT_TTL_MS = 2 * 60 * 60 * 1000;
const MAX_OFFSET_MS = 6 * 60 * 60 * 1000;
const MAX_EVENTS = 20;
const MAX_BATCH_BYTES = 4 * 1024;
const ENDPOINT = '/join-events';

const FAILURE_EVENTS = new Set(['sdk_missing', 'init_failed', 'still_waiting']);

export const FUNNEL_EVENTS = [
  'page_loaded',
  'form_viewed',
  'details_submitted',
  'confirm_viewed',
  'confirmed',
  'avcheck_viewed',
  'av_ready',
  'av_denied',
  'outside_window',
  'join_ready',
  'start_clicked',
  'sdk_missing',
  'init_failed',
  'still_waiting',
  'widget_appeared',
  'task_created',
  'notification_join_call',
  'agent_connected',
  'page_hidden',
  'page_visible',
  'offline',
  'call_ended',
] as const;

export type FunnelEventName = (typeof FUNNEL_EVENTS)[number];
export type FunnelReason =
  'camera' | 'microphone' | 'speaker' | 'too_early' | 'closed' | 'user' | 'dropped';
export type FunnelClient = 'safari' | 'chrome' | 'samsung' | 'other';

interface QueuedEvent {
  seq: number;
  e: FunnelEventName;
  t: number;
  r?: FunnelReason;
}

interface AttemptState {
  attemptId: string;
  startedAt: number;
  nextSeq: number;
  events: QueuedEvent[];
}

interface PreparedBatch {
  generation: number;
  body: string;
  hash: string;
  seqs: number[];
}

export interface FunnelReporterDeps {
  enabled: boolean;
  inFrame: boolean;
  now: () => number;
  storage: Storage | null;
  fetchImpl: typeof fetch;
  hashBody: (body: string) => Promise<string>;
  randomUUID: (() => string) | null;
  client: FunnelClient;
  inApp: boolean;
  build: string;
  /** Test hook. Production uses document and window. */
  signals?: {
    document: Document;
    window: Window;
  };
}

export interface FunnelReporter {
  report(event: FunnelEventName, reason?: FunnelReason): void;
}

export function createFunnelReporter(deps: FunnelReporterDeps): FunnelReporter {
  let state = readState(deps);
  let generation = 0;
  let prepared: PreparedBatch | null = null;
  let sending = false;
  let signalsArmed = false;

  if (state && state.events.length > 0) {
    schedulePrepare(true);
  }

  function report(event: FunnelEventName, reason?: FunnelReason): void {
    try {
      enqueue(event, reason);
    } catch {
      // A failed report must never block the patient.
    }
  }

  function enqueue(event: FunnelEventName, reason?: FunnelReason): void {
    if (!deps.enabled || deps.inFrame) return;
    if (event === 'start_clicked') armSignals();
    const attempt = ensureAttempt();
    if (!attempt) return;
    const queued: QueuedEvent = {
      seq: attempt.nextSeq,
      e: event,
      t: Math.min(MAX_OFFSET_MS, Math.max(0, deps.now() - attempt.startedAt)),
    };
    if (reason) queued.r = reason;
    attempt.nextSeq += 1;
    attempt.events.push(queued);
    persist(deps, state);
    schedulePrepare(false);
  }

  function ensureAttempt(): AttemptState | null {
    if (state && deps.now() - state.startedAt > ATTEMPT_TTL_MS) {
      state = null;
      persist(deps, null);
    }
    if (state) return state;
    if (!deps.randomUUID) return null;
    state = {
      attemptId: deps.randomUUID(),
      startedAt: deps.now(),
      nextSeq: 1,
      events: [],
    };
    persist(deps, state);
    return state;
  }

  function schedulePrepare(flushWhenReady: boolean): void {
    generation += 1;
    prepared = null;
    if (!state || state.events.length === 0) return;
    const myGeneration = generation;
    const batch = selectBatch(state, deps);
    if (!batch) return;
    const flush = flushWhenReady || state.events.some(item => FAILURE_EVENTS.has(item.e));
    deps
      .hashBody(batch.body)
      .then(hash => {
        if (myGeneration !== generation) return;
        prepared = { generation: myGeneration, body: batch.body, hash, seqs: batch.seqs };
        if (flush) sendPrepared();
      })
      .catch(() => {
        // No Web Crypto, or the hash was superseded. Leave the queue for later.
      });
  }

  function sendPrepared(): void {
    const current = prepared;
    if (!current || sending) return;
    prepared = null;
    sending = true;
    void deliver(current).finally(() => {
      sending = false;
    });
  }

  async function deliver(current: PreparedBatch): Promise<void> {
    try {
      const response = await deps.fetchImpl(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-amz-content-sha256': current.hash,
        },
        body: current.body,
        keepalive: true,
        credentials: 'same-origin',
      });
      if (response.status !== 204) {
        if (generation === current.generation) prepared = current;
        return;
      }
      if (state) {
        state.events = state.events.filter(item => !current.seqs.includes(item.seq));
        persist(deps, state);
      }
      if (state && state.events.length > 0) {
        schedulePrepare(true);
      } else {
        generation += 1;
      }
    } catch {
      if (generation === current.generation) prepared = current;
    }
  }

  function armSignals(): void {
    if (signalsArmed) return;
    const target = deps.signals ?? (typeof document === 'undefined' ? null : { document, window });
    if (!target) return;
    signalsArmed = true;
    target.document.addEventListener('visibilitychange', () => {
      try {
        if (target.document.visibilityState === 'hidden') {
          sendPrepared();
          report('page_hidden');
        } else if (target.document.visibilityState === 'visible') {
          report('page_visible');
        }
      } catch {
        // Never throw into the browser.
      }
    });
    target.window.addEventListener('pagehide', () => {
      try {
        sendPrepared();
      } catch {
        // Never throw into the browser.
      }
    });
    target.window.addEventListener('offline', () => {
      try {
        report('offline');
      } catch {
        // Never throw into the browser.
      }
    });
  }

  return { report };
}

export function selectBatch(
  state: AttemptState,
  deps: Pick<FunnelReporterDeps, 'build' | 'client' | 'inApp'>,
  limits: { maxEvents?: number; maxBytes?: number } = {}
): { body: string; seqs: number[] } | null {
  const maxEvents = limits.maxEvents ?? MAX_EVENTS;
  const maxBytes = limits.maxBytes ?? MAX_BATCH_BYTES;
  if (state.events.length === 0) return null;
  const chosen: QueuedEvent[] = [];
  let body = '';
  for (const event of state.events) {
    if (chosen.length === maxEvents) break;
    const candidate = JSON.stringify({
      v: 1,
      attemptId: state.attemptId,
      build: deps.build,
      client: deps.client,
      inApp: deps.inApp,
      events: [...chosen, event].map(toWire),
    });
    if (chosen.length > 0 && byteLength(candidate) > maxBytes) break;
    chosen.push(event);
    body = candidate;
  }
  if (!body) return null;
  return { body, seqs: chosen.map(event => event.seq) };
}

function toWire(event: QueuedEvent): {
  seq: number;
  e: FunnelEventName;
  t: number;
  r?: FunnelReason;
} {
  if (event.r) return { seq: event.seq, e: event.e, t: event.t, r: event.r };
  return { seq: event.seq, e: event.e, t: event.t };
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function readState(deps: FunnelReporterDeps): AttemptState | null {
  if (!deps.storage) return null;
  try {
    const raw = deps.storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AttemptState;
    if (!parsed || typeof parsed.attemptId !== 'string' || !Array.isArray(parsed.events))
      return null;
    if (deps.now() - parsed.startedAt > ATTEMPT_TTL_MS) {
      deps.storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persist(deps: FunnelReporterDeps, next: AttemptState | null): void {
  if (!deps.storage) return;
  try {
    if (!next) {
      deps.storage.removeItem(STORAGE_KEY);
      return;
    }
    deps.storage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        attemptId: next.attemptId,
        startedAt: next.startedAt,
        nextSeq: next.nextSeq,
        events: next.events,
      })
    );
  } catch {
    // Storage blocked. The in-memory queue still covers this page.
  }
}

export function detectClient(ua: string): FunnelClient {
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/Edg\/|OPR\/|Firefox\/|FxiOS/i.test(ua)) return 'other';
  if (/Chrome\/|CriOS/i.test(ua)) return 'chrome';
  if (/Safari/i.test(ua)) return 'safari';
  return 'other';
}

export function detectInApp(ua: string): boolean {
  if (/FBAN|FBAV|Instagram|Line\/|Twitter|LinkedInApp|TikTok|Snapchat|GSA\//i.test(ua)) return true;
  return /iPhone|iPad|iPod/i.test(ua) && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua);
}

function postingEnabled(): boolean {
  if (import.meta.env.DEV) return false;
  if (import.meta.env.VITE_FUNNEL_EVENTS === 'false') return false;
  return true;
}

function pageIsFrame(): boolean {
  try {
    return window.top !== window;
  } catch {
    return true;
  }
}

async function sha256Hex(body: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('Web Crypto is unavailable');
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(body));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

const reporter = createFunnelReporter({
  enabled: postingEnabled(),
  inFrame: typeof window === 'undefined' ? false : pageIsFrame(),
  now: () => Date.now(),
  storage: typeof sessionStorage === 'undefined' ? null : sessionStorage,
  fetchImpl: (...args) => fetch(...args),
  hashBody: sha256Hex,
  randomUUID: typeof crypto !== 'undefined' && crypto.randomUUID ? () => crypto.randomUUID() : null,
  client: typeof navigator === 'undefined' ? 'other' : detectClient(navigator.userAgent),
  inApp: typeof navigator === 'undefined' ? false : detectInApp(navigator.userAgent),
  build: import.meta.env.VITE_BUILD_ID || 'unknown',
});

export function reportFunnel(event: FunnelEventName, reason?: FunnelReason): void {
  try {
    reporter.report(event, reason);
  } catch {
    // A failed report must never block the patient.
  }
}
